import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  Cell,
  Area,
  AreaChart,
} from 'recharts';
import { GlassCard } from '../ui/GlassCard';
import { GitBranch, TrendingUp, Filter } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { DeflectionTrend, IssueDeflection, FunnelStage } from '../../hooks/useMetricsData';

// ─── Custom Tooltips ─────────────────────────────────────────────────

function TrendTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; dataKey: string; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel px-3 py-2 rounded-xl border border-white/10 shadow-xl">
      <p className="text-[10px] text-text-muted mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-xs text-text-muted capitalize">
            {p.dataKey === 'rate' ? 'Deflection Rate' : '7-Day Moving Avg'}:
          </span>
          <span className="text-xs font-semibold text-text-primary tabular-nums">
            {p.value.toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
}

function BarTooltip({ active, payload }: { active?: boolean; payload?: Array<{ value: number; payload: { issue: string; rate: number } }> }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel px-3 py-2 rounded-xl border border-white/10 shadow-xl">
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-muted">{payload[0].payload.issue}:</span>
        <span className="text-xs font-semibold text-text-primary tabular-nums">
          {payload[0].value}%
        </span>
      </div>
    </div>
  );
}

// ─── Funnel Chart ────────────────────────────────────────────────────

function FunnelChart({ data }: { data: FunnelStage[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const colors = [
    'from-vokentra/30 to-vokentra/10',
    'from-info/30 to-info/10',
    'from-success/30 to-success/10',
    'from-warning/30 to-warning/10',
  ];

  const barColors = ['#e60000', '#00bfff', '#00d084', '#ffa500'];

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-vokentra" />
          <span className="text-sm font-semibold text-text-primary">Resolution Funnel</span>
        </div>
      }
    >
      <div ref={ref} className="space-y-3 py-2">
        {data.map((stage, i) => {
          const prevValue = i > 0 ? data[i - 1].value : stage.value;
          const dropOff = i > 0 ? ((prevValue - stage.value) / prevValue * 100).toFixed(1) : null;

          return (
            <motion.div
              key={stage.name}
              className="space-y-1"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.15, duration: 0.4 }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-secondary">{stage.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-text-primary tabular-nums">
                    {stage.value}%
                  </span>
                  {dropOff && (
                    <span className="text-[10px] text-warning">-{dropOff}%</span>
                  )}
                </div>
              </div>
              <div className="relative h-8 rounded-lg bg-white/[0.04] overflow-hidden">
                <motion.div
                  className={cn(
                    'absolute inset-y-0 left-0 rounded-lg bg-gradient-to-r',
                    colors[i]
                  )}
                  initial={{ width: 0 }}
                  animate={isInView ? { width: `${stage.percentage}%` } : {}}
                  transition={{
                    delay: i * 0.15 + 0.2,
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                />
                <div className="absolute inset-0 flex items-center px-3">
                  <span className="text-[10px] font-medium text-text-primary/80 tabular-nums">
                    {stage.percentage}%
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </GlassCard>
  );
}

// ─── Deflection by Issue Type ────────────────────────────────────────

function DeflectionByIssue({ data }: { data: IssueDeflection[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const barColor = (rate: number): string => {
    if (rate >= 90) return '#00d084';
    if (rate >= 80) return '#00bfff';
    if (rate >= 70) return '#ffa500';
    return '#e60000';
  };

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-success" />
          <span className="text-sm font-semibold text-text-primary">
            Deflection by Issue Type
          </span>
        </div>
      }
    >
      <div ref={ref} className="h-[260px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              horizontal={false}
            />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="issue"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip content={<BarTooltip />} />
            <Bar dataKey="rate" radius={[0, 6, 6, 0]} maxBarSize={20}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColor(entry.rate)} />
              ))}
              {/* Labels */}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-1 justify-center">
        {[
          { color: '#00d084', label: '90%+ Excellent' },
          { color: '#00bfff', label: '80-89% Good' },
          { color: '#ffa500', label: '70-79% Fair' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── 30-Day Trend ────────────────────────────────────────────────────

function DeflectionTrendChart({ data }: { data: DeflectionTrend[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-warning" />
          <span className="text-sm font-semibold text-text-primary">
            30-Day Deflection Trend
          </span>
        </div>
      }
    >
      <div ref={ref} className="h-[260px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00d084" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#00d084" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: string) => {
                const d = new Date(v);
                return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}`;
              }}
              interval={4}
            />
            <YAxis
              tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              domain={[60, 90]}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip content={<TrendTooltip />} />
            <ReferenceLine
              y={75}
              stroke="#00d084"
              strokeDasharray="6 3"
              strokeWidth={1}
              label={{
                value: 'Target: 75%',
                position: 'insideTopRight',
                fill: '#00d084',
                fontSize: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="rate"
              stroke="#00d084"
              strokeWidth={2}
              fill="url(#trendGrad)"
              dot={false}
              isAnimationActive={true}
              animationDuration={1500}
            />
            <Line
              type="monotone"
              dataKey="movingAvg"
              stroke="#ffa500"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              isAnimationActive={true}
              animationDuration={1500}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-1 justify-center">
        {[
          { key: 'rate', color: '#00d084', label: 'Daily Deflection Rate' },
          { key: 'ma', color: '#ffa500', label: '7-Day Moving Average' },
        ].map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span
              className={cn(
                'w-3 h-0.5 rounded-full',
                item.key === 'ma' && 'border-t border-dashed'
              )}
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

// ─── Exported Component ──────────────────────────────────────────────

interface DeflectionAnalyticsProps {
  deflectionTrend: DeflectionTrend[];
  issueDeflection: IssueDeflection[];
  funnel: FunnelStage[];
  className?: string;
}

export function DeflectionAnalytics({
  deflectionTrend,
  issueDeflection,
  funnel,
  className,
}: DeflectionAnalyticsProps) {
  return (
    <div className={cn('grid grid-cols-1 lg:grid-cols-3 gap-4', className)}>
      <FunnelChart data={funnel} />
      <DeflectionByIssue data={issueDeflection} />
      <DeflectionTrendChart data={deflectionTrend} />
    </div>
  );
}

export default DeflectionAnalytics;
